import { useAlert } from '@ads/components-react';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getApiV1ExamRevisionBotexamidQueryKey,
  useGetApiV1ExamRevisionBotexamid,
  usePostApiV1ExamRevisionReview,
  usePutApiV1ExamRevisionQuestion,
} from '@/shared/api/generated';
import type { QuestionTypeFilter, RevisionQuestion } from '../types/revision';
import { QUESTION_TYPE_FILTERS } from '../utils/questionType';
import {
  applyReviewEdits,
  toReviewQuestionSavePayload,
  toRevisionQuestion,
} from '../utils/revisionMapper';

/**
 * Tamanho de página usado na revisão. As questões são carregadas de uma vez
 * (paginação por bloco único) para permitir filtro por tipo client-side,
 * navegação livre entre questões e submissão do modelo completo no `POST /review`.
 */
const REVIEW_PAGE_SIZE = 100;

/** Callbacks opcionais das ações de escrita da revisão. */
interface RevisionReviewCallbacks {
  readonly onSuccess?: () => void;
}

/**
 * Carrega e edita a revisão de questões de uma prova gerada por IA a partir de
 * `GET /api/v1/exam/revision/{botExamId}` (`ClassroomDisciplineBotExamReviewViewModel`),
 * mantendo um buffer local de edições. Expõe:
 *
 * - **Salvar questão** (`saveCurrentQuestion`) → `PUT /api/v1/exam/revision/question`
 *   (persistência individual da questão em foco).
 * - **Finalizar** (`submitReview`) → `POST /api/v1/exam/revision/review` (envia o
 *   modelo inteiro com as edições aplicadas por patch, preservando campos não editados).
 *
 * Toda falha (consulta ou mutação) dispara um alert de erro via `useAlert`.
 *
 * @param botExamId - ID da prova bot em revisão
 * @returns Questões, navegação/filtro, buffer de edição, ações de escrita e estados
 *
 * @example
 * ```tsx
 * const { current, saveCurrentQuestion, submitReview } = useRevisionReview(botExamId);
 * ```
 */
export function useRevisionReview(botExamId: number) {
  const queryClient = useQueryClient();
  const { alert } = useAlert();
  const { t } = useTranslation('examRevision');

  const [typeFilter, setTypeFilterState] = useState<QuestionTypeFilter>('all');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [overrides, setOverrides] = useState<Record<number, Partial<RevisionQuestion>>>({});
  const [deletedIds, setDeletedIds] = useState<number[]>([]);

  const query = useGetApiV1ExamRevisionBotexamid(
    botExamId,
    { pageNumber: 1, pageSize: REVIEW_PAGE_SIZE },
    { query: { enabled: botExamId > 0, placeholderData: keepPreviousData } },
  );

  useEffect(() => {
    if (query.isError) alert({ alertType: 'warning', title: t('review.toast.loadError') });
  }, [query.isError, alert, t]);

  const review = query.data ?? null;
  const rawQuestions = review?.questions ?? [];
  const botExamLangId = review?.botExamLangId ?? null;

  const questions = rawQuestions
    .map((dto, i) => toRevisionQuestion(dto, i + 1))
    .filter(question => !deletedIds.includes(question.id))
    .map(question => ({ ...question, ...overrides[question.id] }));

  // Filtros disponíveis: "Todos" + apenas os tipos efetivamente presentes na prova.
  const presentTypes = new Set(questions.map(question => question.questionType));
  const availableFilters = QUESTION_TYPE_FILTERS.filter(
    filter => filter === 'all' || presentTypes.has(filter),
  );

  const filtered =
    typeFilter === 'all'
      ? questions
      : questions.filter(question => question.questionType === typeFilter);

  const total = filtered.length;
  const safeIndex = Math.min(currentIndex, Math.max(0, total - 1));
  const current = filtered[safeIndex] ?? null;

  const invalidateReview = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: getApiV1ExamRevisionBotexamidQueryKey(botExamId),
      }),
    [queryClient, botExamId],
  );

  const goTo = useCallback((index: number) => {
    setCurrentIndex(Math.max(0, index));
  }, []);

  // Ao trocar o filtro de tipo, volta para a primeira questão do conjunto filtrado
  // (a numeração e o conteúdo passam a refletir a nova seleção).
  const setTypeFilter = useCallback((type: QuestionTypeFilter) => {
    setTypeFilterState(type);
    setCurrentIndex(0);
  }, []);

  const updateQuestion = useCallback((id: number, patch: Partial<RevisionQuestion>) => {
    setOverrides(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  }, []);

  const deleteQuestion = useCallback((id: number) => {
    setDeletedIds(prev => [...prev, id]);
    setCurrentIndex(prev => Math.max(0, prev - 1));
  }, []);

  const saveMutation = usePutApiV1ExamRevisionQuestion({
    mutation: {
      // Não invalida a query aqui: o refetch do grid só deve ocorrer ao finalizar a
      // revisão. As edições salvas já vivem no buffer local (`overrides`), então
      // recarregar a cada salvamento reiniciaria o estado da tela sem necessidade.
      onSuccess: () => {
        alert({ alertType: 'success', title: t('review.toast.saveSuccess') });
      },
      onError: () => alert({ alertType: 'warning', title: t('review.toast.saveError') }),
    },
  });

  const submitMutation = usePostApiV1ExamRevisionReview({
    mutation: {
      onSuccess: () => {
        alert({ alertType: 'success', title: t('review.toast.finalizeSuccess') });
        void invalidateReview();
      },
      onError: () => alert({ alertType: 'warning', title: t('review.toast.finalizeError') }),
    },
  });

  const saveCurrentQuestion = useCallback(
    (callbacks?: RevisionReviewCallbacks) => {
      if (!current || botExamLangId === null) return;
      const dto = rawQuestions.find(item => (item.id ?? -1) === current.id);
      if (!dto) return;
      const payload = toReviewQuestionSavePayload(dto, overrides[current.id], botExamLangId);
      saveMutation.mutate(
        { data: payload },
        {
          // Sem invalidar/refetch aqui: mantemos o override no buffer local para que
          // a edição salva continue visível (o grid só é recarregado ao finalizar).
          onSuccess: () => {
            callbacks?.onSuccess?.();
          },
        },
      );
    },
    [current, botExamLangId, rawQuestions, overrides, saveMutation],
  );

  const submitReview = useCallback(
    (callbacks?: RevisionReviewCallbacks) => {
      if (!review) return;
      const questionsPayload = rawQuestions
        .filter(dto => !deletedIds.includes(dto.id ?? -1))
        .map(dto => applyReviewEdits(dto, overrides[dto.id ?? -1]));
      submitMutation.mutate(
        { data: { ...review, questions: questionsPayload } },
        {
          onSuccess: () => {
            setOverrides({});
            setDeletedIds([]);
            callbacks?.onSuccess?.();
          },
        },
      );
    },
    [review, rawQuestions, deletedIds, overrides, submitMutation],
  );

  return {
    review,
    questions: filtered,
    current,
    currentIndex: safeIndex,
    total,
    typeFilter,
    /** "Todos" + apenas os tipos de questão presentes na prova carregada. */
    availableFilters,
    setTypeFilter,
    goTo,
    updateQuestion,
    deleteQuestion,
    saveCurrentQuestion,
    submitReview,
    /** Título real da prova (cabeçalho/breadcrumb). */
    title: review?.title ?? null,
    /** Instrução geral da prova. */
    instruction: review?.instruction ?? null,
    /** ID do idioma pt-BR da prova (necessário para adicionar/salvar questões). */
    botExamLangId,
    isSaving: saveMutation.isPending,
    isSubmitting: submitMutation.isPending,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: () => {
      void query.refetch();
    },
  } as const;
}
